// K-Crypto — client-side cryptographic evidence engine (WebCrypto / SHA-256).
// NOTE: This is LOCAL cryptographic evidence only. It is NOT blockchain
// verification, NOT RFC 3161 timestamping, NOT third-party notarization, and
// NOT government verification.

import { GENESIS_HASH, DEFAULT_ORIGIN, DEFAULT_FRAMEWORK } from "./constants";

// Deterministic JSON canonicalization: recursively sort object keys.
function sortValue(v) {
  if (Array.isArray(v)) return v.map(sortValue);
  if (v && typeof v === "object") {
    return Object.keys(v)
      .sort()
      .reduce((acc, k) => {
        acc[k] = sortValue(v[k]);
        return acc;
      }, {});
  }
  return v;
}

export function canonicalize(value) {
  return JSON.stringify(sortValue(value));
}

export async function sha256Hex(str) {
  const data = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function genId(prefix = "SLNK") {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${prefix}-${hex.toUpperCase()}`;
}

// Fields that are cryptographically bound into the record hash.
export const HASH_FIELDS = [
  "timestamp",
  "event_id",
  "origin",
  "payload",
  "payload_type",
  "framework",
  "layer",
  "previous_hash",
  "drift_status",
  "notes",
];

function contentSubset(r) {
  const o = {};
  const safe = r && typeof r === "object" ? r : {};
  HASH_FIELDS.forEach((k) => {
    o[k] = safe[k];
  });
  return o;
}

// RAW TEXT hashing: fixed-order, byte-sensitive serialization (whitespace and
// formatting inside the payload change the digest). Distinct from CANONICAL.
function rawSerialize(r) {
  const safe = r && typeof r === "object" ? r : {};
  return HASH_FIELDS.map((k) => {
    const v = safe[k];
    return `${k}=${v && typeof v === "object" ? JSON.stringify(v) : String(v)}`;
  }).join("\n");
}

export function normalizeHashMode(mode) {
  return mode === "raw" ? "raw" : "canonical";
}

const HEX64 = /^[0-9a-f]{64}$/i;

// Structural validation for imported / stored receipts (defends against
// malformed or corrupted JSON).
export function isValidReceipt(r) {
  return (
    !!r &&
    typeof r === "object" &&
    typeof r.sha256 === "string" &&
    HEX64.test(r.sha256) &&
    typeof r.event_id === "string" &&
    typeof r.timestamp === "string" &&
    r.payload !== undefined
  );
}

export async function computeHash(record) {
  const mode = normalizeHashMode(record && record.hash_mode);
  const canon = mode === "raw" ? rawSerialize(record) : canonicalize(contentSubset(record));
  const hash = await sha256Hex(canon);
  return { canon, hash, mode };
}

// Verify a single receipt by recomputing its hash from current content.
export async function verifyRecord(record) {
  if (!record || typeof record.sha256 !== "string") {
    return { ok: false, computed: null, reason: "Missing sha256 field" };
  }
  const { hash } = await computeHash(record);
  return {
    ok: hash === record.sha256,
    computed: hash,
    reason: hash === record.sha256 ? "Recomputed hash matches receipt" : "Recomputed hash differs from receipt",
  };
}

export async function createRecord(input, previousHash) {
  const base = {
    timestamp: new Date().toISOString(),
    event_id: genId("EVT"),
    origin: input.origin || DEFAULT_ORIGIN,
    payload: input.payload,
    payload_type: input.payload_type || "Test Payload",
    framework: input.framework || DEFAULT_FRAMEWORK,
    layer: input.layer ?? 1,
    previous_hash: previousHash || GENESIS_HASH,
    drift_status: input.drift_status || "NONE",
    notes: input.notes || "",
    hash_mode: normalizeHashMode(input.hash_mode),
  };
  const { canon, hash, mode } = await computeHash(base);
  return {
    ...base,
    id: base.event_id,
    canonical_payload: canon,
    sha256: hash,
    hash_mode: mode,
    integrity_status: "VERIFIED",
    verification_method: `Local SHA-256 (WebCrypto) recomputation · ${mode === "raw" ? "RAW TEXT" : "CANONICAL JSON"} mode`,
    source_tag: input.source_tag || "local",
    telemetry: input.telemetry || null,
    meta: input.meta || null,
    signature: input.signature || null,
  };
}

// Verify an entire ordered chain.
// Returns array parallel to records: { selfOk, linkOk, computed }.
export async function verifyChain(records) {
  const list = Array.isArray(records) ? records : [];
  const results = [];
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    if (!r || typeof r !== "object") {
      results.push({ selfOk: false, linkOk: false, computed: null, expectedPrev: null });
      continue;
    }
    const { hash } = await computeHash(r);
    const selfOk = hash === r.sha256;
    const expectedPrev = i === 0 ? GENESIS_HASH : list[i - 1].sha256;
    const linkOk = r.previous_hash === expectedPrev;
    results.push({ selfOk, linkOk, computed: hash, expectedPrev });
  }
  return results;
}

export { GENESIS_HASH };
